/**
 * Umami analytics loader.
 *
 * Two tiers, with different privacy treatment:
 *   - Pageview tracking (`script.js`): cookieless and anonymized, loaded
 *     unconditionally (in production). No consent required.
 *   - Heatmap / session recorder (`recorder.js`): more invasive, loaded ONLY
 *     after the user opts in. Consent is persisted in localStorage.
 *
 * SPA route changes are auto-tracked by Umami via the History API, so no
 * manual pageview shim is needed.
 *
 * Tracking is enabled only in production builds by default. Set
 * `VITE_UMAMI_ENABLED=true` (or `false`) to override per environment.
 *
 * Required env:
 *   VITE_UMAMI_URL         e.g. https://stats.asymetree.com
 *   VITE_UMAMI_WEBSITE_ID  the Umami website UUID
 */

const UMAMI_URL = import.meta.env.VITE_UMAMI_URL as string | undefined;
const WEBSITE_ID = import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined;

export const HEATMAP_CONSENT_KEY = 'saqia_heatmap_consent';
export type HeatmapConsent = 'granted' | 'denied';

/** Event fired on `window` whenever heatmap consent changes. */
export const HEATMAP_CONSENT_EVENT = 'saqia:heatmap-consent-changed';

function isEnabled(): boolean {
  const flag = import.meta.env.VITE_UMAMI_ENABLED as string | undefined;
  if (flag !== undefined) return flag === 'true';
  return import.meta.env.PROD;
}

function isConfigured(): boolean {
  if (!UMAMI_URL || !WEBSITE_ID) {
    console.warn(
      '[analytics] Umami disabled: VITE_UMAMI_URL / VITE_UMAMI_WEBSITE_ID not set'
    );
    return false;
  }
  return true;
}

function scriptSrc(file: string): string {
  return `${(UMAMI_URL as string).replace(/\/$/, '')}/${file}`;
}

function injectScript(src: string): void {
  if (document.querySelector(`script[src="${src}"]`)) return; // idempotent
  const script = document.createElement('script');
  script.defer = true;
  script.src = src;
  script.setAttribute('data-website-id', WEBSITE_ID as string);
  document.head.appendChild(script);
}

/** Read the persisted heatmap consent choice (`null` if undecided). */
export function getHeatmapConsent(): HeatmapConsent | null {
  try {
    const value = localStorage.getItem(HEATMAP_CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

/** Persist a heatmap consent choice and apply it (loads the recorder on grant). */
export function setHeatmapConsent(value: HeatmapConsent): void {
  try {
    localStorage.setItem(HEATMAP_CONSENT_KEY, value);
  } catch {
    /* storage unavailable — consent simply won't persist */
  }
  window.dispatchEvent(
    new CustomEvent(HEATMAP_CONSENT_EVENT, { detail: value })
  );
  if (value === 'granted') enableHeatmaps();
}

/** Load the heatmap/session recorder. No-op unless enabled + configured. */
export function enableHeatmaps(): void {
  if (!isEnabled() || !isConfigured()) return;
  injectScript(scriptSrc('recorder.js'));
}

let initialized = false;

/**
 * Load cookieless pageview tracking, and the heatmap recorder too if the user
 * has previously granted consent. Call once at app startup.
 */
export function initAnalytics(): void {
  if (initialized) return;
  if (!isEnabled() || !isConfigured()) return;

  initialized = true;
  injectScript(scriptSrc('script.js')); // pageviews + events (no consent needed)

  if (getHeatmapConsent() === 'granted') enableHeatmaps();
}
